import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  FileText,
  DollarSign,
  Calendar,
  ShoppingCart,
  CheckCircle,
  Clock,
  AlertCircle,
  FileDown,
  Info,
  XCircle,
  Banknote,
  CreditCard,
  GripVertical
} from 'lucide-react';
import { salesInvoicesAPI, salesOrdersAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { productsAPI } from '../services/api';
import { paymentsAPI } from '../services/api';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import { generateSalesInvoicePDF } from '../utils/invoicePDF';
import { formatDateOnlyFr } from '../utils/dateFormat';

const defaultForm = {
  sales_id: '',
  numero_facture: '',
  amount: '',
  date: '',
  status: 'pending',
  items: []
};

const defaultItem = {
  product_id: null,
  custom_product_name: '',
  item_type: 'monture',
  side: 'none',
  quantity: 1,
  unit_price: ''
};

function newInvoiceRowId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeInvoiceItemForForm(item) {
  return {
    ...item,
    clientRowId: item.clientRowId || (item.id != null ? `db-${item.id}` : newInvoiceRowId())
  };
}

const VentesFactures = () => {
  const [invoices, setInvoices] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', mode: 'espece', note: '' });
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editPayment, setEditPayment] = useState({ amount: '', mode: 'espece', note: '' });
  const [saleItems, setSaleItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  
  // PDF Invoice ID Modal state
  const [showInvoiceIdModal, setShowInvoiceIdModal] = useState(false);
  const [pendingInvoiceData, setPendingInvoiceData] = useState(null);
  const [customInvoiceId, setCustomInvoiceId] = useState('');
  const [useCustomId, setUseCustomId] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

  const getProductName = (id) => {
    const product = products.find(p => p.id === id);
    return product ? product.name : `Produit ${id}`;
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await salesInvoicesAPI.getSalesInvoices();
      setInvoices(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des factures');
    }
    setLoading(false);
  };

  // Fetch all sales orders for dropdown (paginated; default API limit was 200)
  const fetchSalesOrders = async () => {
    try {
      const orders = await salesOrdersAPI.getAllSalesOrdersForSelect();
      setSalesOrders(orders);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchSalesOrders();
    productsAPI.getProducts().then(res => setProducts(res.data)).catch(() => setProducts([]));
  }, []);

  // Function to load sales order items
  const loadSalesOrderItems = async (salesOrderId) => {
    if (!salesOrderId) {
      setForm(f => ({ ...f, items: [] }));
      return;
    }

    setLoadingItems(true);
    try {
      const res = await salesOrdersAPI.getSalesOrder(salesOrderId);
      const orderItems = res.data.items || [];
      
      // Convert order items to invoice items format
      const invoiceItems = orderItems.map(item => ({
        product_id: item.product_id,
        custom_product_name: item.product_name || getProductName(item.product_id),
        item_type: item.item_type,
        side: item.side || 'none',
        quantity: item.quantity,
        unit_price: parseFloat(item.price || 0),
        clientRowId: newInvoiceRowId()
      }));
      
      // Calculate total amount (treat empty unit_price as 0)
      const totalAmount = invoiceItems.reduce((sum, item) => {
        const price = item.unit_price === '' || item.unit_price === null || item.unit_price === undefined ? 0 : parseFloat(item.unit_price) || 0;
        return sum + (item.quantity * price);
      }, 0);
      
      setForm(f => ({ 
        ...f, 
        items: invoiceItems,
        amount: totalAmount.toString()
      }));
      
      // Show success message
      if (invoiceItems.length > 0) {
        toast.success(`${invoiceItems.length} article(s) chargé(s) depuis la commande`);
      }
    } catch (err) {
      console.error('Error fetching sales order items:', err);
      setForm(f => ({ ...f, items: [] }));
      toast.error('Erreur lors du chargement des articles de la commande');
    } finally {
      setLoadingItems(false);
    }
  };

  // Helper: options for Select
  const frameOptions = (products || []).filter(p => p.type === 'frames' || (p.category_name && p.category_name.toLowerCase().includes('monture')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));
  const lensOptions = (products || []).filter(p => p.type === 'glasses' || (p.category_name && p.category_name.toLowerCase().includes('verre')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));
  const accessoryOptions = (products || []).filter(p => p.type === 'accessories' || (p.category_name && p.category_name.toLowerCase().includes('accessoire')))
    .map(p => ({ value: p.id, label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}` }));

  const paymentModes = [
    { value: 'espece', label: 'Espèce' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'carte_bancaire', label: 'Carte bancaire' }
  ];

  const itemTypes = [
    { value: 'monture', label: 'Monture' },
    { value: 'verre', label: 'Verre' },
    { value: 'accessoire', label: 'Accessoire' },
    { value: 'lentilles', label: 'Lentilles' }
  ];

  const sideOptions = [
    { value: 'none', label: 'Aucun' },
    { value: 'left', label: 'Gauche' },
    { value: 'right', label: 'Droite' }
  ];

  // Item management functions
  const addItem = () => {
    setForm(f => ({ 
      ...f, 
      items: [...f.items, { ...defaultItem, clientRowId: newInvoiceRowId() }] 
    }));
  };

  const moveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setForm(f => {
      const next = [...f.items];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return { ...f, items: next };
    });
  };

  const handleItemDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleItemDrop = (e, dropIndex) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(from)) return;
    moveItem(from, dropIndex);
  };

  const updateItem = (index, field, value) => {
    setForm(f => {
      const newItems = [...f.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Auto-fill product name if product is selected
      if (field === 'product_id' && value) {
        const product = products.find(p => p.id === parseInt(value));
        if (product) {
          newItems[index].custom_product_name = product.name;
          newItems[index].unit_price = parseFloat(product.price || 0);
        }
      }
      
      // Recalculate total amount (treat empty unit_price as 0)
      const totalAmount = newItems.reduce((sum, item) => {
        const price = item.unit_price === '' || item.unit_price === null || item.unit_price === undefined ? 0 : parseFloat(item.unit_price) || 0;
        return sum + (item.quantity * price);
      }, 0);
      
      return { 
        ...f, 
        items: newItems,
        amount: totalAmount.toString()
      };
    });
  };

  const removeItem = (index) => {
    setForm(f => {
      const newItems = f.items.filter((_, i) => i !== index);
      const totalAmount = newItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      
      return { 
        ...f, 
        items: newItems,
        amount: totalAmount.toString()
      };
    });
  };

  const loadInvoicePayments = async (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentLoading(true);
    try {
      const payments = (await paymentsAPI.getPaymentsForRef('sales_invoice', invoice.id)).data;
      setInvoicePayments(payments);
    } catch { setInvoicePayments([]); }
    setPaymentLoading(false);
    setShowPaymentModal(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.createPayment({
        reference_type: 'sales_invoice',
        reference_id: selectedInvoice.id,
        amount: newPayment.amount,
        mode: newPayment.mode,
        note: newPayment.note
      });
      setNewPayment({ amount: '', mode: 'espece', note: '' });
      const payments = (await paymentsAPI.getPaymentsForRef('sales_invoice', selectedInvoice.id)).data;
      setInvoicePayments(payments);
    } catch {}
    setPaymentLoading(false);
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.deletePayment(id);
      const payments = (await paymentsAPI.getPaymentsForRef('sales_invoice', selectedInvoice.id)).data;
      setInvoicePayments(payments);
    } catch {}
    setPaymentLoading(false);
  };

  const startEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setEditPayment({ amount: payment.amount, mode: payment.mode, note: payment.note || '' });
  };
  
  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setEditPayment({ amount: '', mode: 'espece', note: '' });
  };
  
  const handleSaveEditPayment = async (e) => {
    e.preventDefault();
    if (!editingPaymentId) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.updatePayment(editingPaymentId, {
        amount: editPayment.amount,
        mode: editPayment.mode,
        note: editPayment.note
      });
      setEditingPaymentId(null);
      setEditPayment({ amount: '', mode: 'espece', note: '' });
      const payments = (await paymentsAPI.getPaymentsForRef('sales_invoice', selectedInvoice.id)).data;
      setInvoicePayments(payments);
    } catch {}
    setPaymentLoading(false);
  };

  const getPaymentStatus = (invoice, payments) => {
    const paid = payments.reduce((sum, p) => sum + Number(p.amount), 0);
    if (paid === 0) return { label: 'Non payé', color: 'danger' };
    if (paid < (invoice.amount || 0)) return { label: 'Partiel', color: 'warning' };
    return { label: 'Payé', color: 'success' };
  };

  // Handle form input
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const payload = {
        sales_id: form.sales_id,
        amount: form.amount,
        date: form.date,
        status: form.status,
        items: form.items.map(({ clientRowId, ...rest }) => rest),
        numero_facture: typeof form.numero_facture === 'string' ? form.numero_facture.trim() : ''
      };

      if (editingId) {
        await salesInvoicesAPI.updateSalesInvoice(editingId, payload);
        toast.success('Facture modifiée');
      } else {
        await salesInvoicesAPI.createSalesInvoice(payload);
        toast.success('Facture ajoutée');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchInvoices();
    } catch (err) {
      console.error('Error submitting invoice:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (invoice) => {
    console.log('Editing invoice:', invoice);
    const itemsOrdered = [...(invoice.items || [])].sort((a, b) => {
      const sa = a.sort_order != null ? Number(a.sort_order) : 0;
      const sb = b.sort_order != null ? Number(b.sort_order) : 0;
      if (sa !== sb) return sa - sb;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
    setForm({
      sales_id: invoice.sales_id ? invoice.sales_id.toString() : '',
      numero_facture: invoice.numero_facture != null ? String(invoice.numero_facture) : '',
      amount: invoice.amount,
      date: invoice.date ? invoice.date.slice(0, 10) : '',
      status: invoice.status,
      items: itemsOrdered.map(normalizeInvoiceItemForForm)
    });
    setEditingId(invoice.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await salesInvoicesAPI.deleteSalesInvoice(id);
      toast.success('Facture supprimée');
      fetchInvoices();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setLoadingItems(false);
    setShowForm(true);
  };

  // Helper to get sale info
  const getSaleInfo = (salesId) => {
    const sale = salesOrders.find((s) => s.id === salesId);
    return sale ? `Commande #${sale.id} - ${sale.client_name || 'Client'}` : salesId;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'En attente' },
      paid: { variant: 'success', label: 'Payée' },
      cancelled: { variant: 'danger', label: 'Annulée' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <ModernBadge variant={config.variant}>{config.label}</ModernBadge>;
  };

  const filteredInvoices = (invoices || []).filter(invoice => {
    const saleInfo = getSaleInfo(invoice.sales_id);
    return String(saleInfo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.amount || '').includes(searchTerm) ||
           String(invoice.date || '').includes(searchTerm) ||
           String(invoice.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.id ?? '').includes(searchTerm) ||
           String(invoice.numero_facture ?? '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => formatDateOnlyFr(dateString);

  const salesOptions = (salesOrders || []).map(sale => ({
    value: sale.id.toString(),
    label: `Commande #${sale.id} - ${sale.client_name || 'Client'} - ${formatCurrency(sale.total || 0)}`
  }));

  const statusOptions = [
    { value: 'pending', label: 'En attente' },
    { value: 'paid', label: 'Payée' },
    { value: 'cancelled', label: 'Annulée' }
  ];

  const tableColumns = [
    {
      key: 'numero_facture',
      label: 'N° facture',
      render: (value, invoice) => {
        const custom = value != null && String(value).trim() !== '';
        return (
          <span className={`font-semibold text-neutral-900 ${custom ? '' : 'tabular-nums text-neutral-500'}`}>
            {custom ? String(value).trim() : String(invoice.id)}
          </span>
        );
      }
    },
    {
      key: 'sales_id',
      label: 'Vente',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <ShoppingCart className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{getSaleInfo(value)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, invoice) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(invoice)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="secondary"
            size="sm"
            icon={<ShoppingCart className="w-4 h-4" />}
            onClick={() => loadInvoicePayments(invoice)}
          >
            Paiements
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<FileDown className="w-4 h-4" />}
            onClick={async () => {
              await generateInvoicePDF(invoice);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
          >
            PDF
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(invoice.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalAmount = (invoices || []).reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0);
  const pendingInvoices = (invoices || []).filter(i => i.status === 'pending').length;
  const paidInvoices = (invoices || []).filter(i => i.status === 'paid').length;
  const cancelledInvoices = (invoices || []).filter(i => i.status === 'cancelled').length;

  const handleExportPayments = async () => {
    try {
      const response = await paymentsAPI.exportPayments({ reference_type: 'sales_invoice' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payments_sales_invoices.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Erreur lors de l\'export des paiements');
    }
  };

  // Helper to generate PDF invoice for a facture
  const generateInvoicePDF = async (invoice) => {
    // Store invoice data for later use
    setPendingInvoiceData({
      invoice,
      clientName: (salesOrders.find(s => s.id === invoice.sales_id) || {}).client_name || `Client ${invoice.sales_id}`,
      clientId: invoice.sales_id
    });
    
    // Reset modal state (préremplir si un numéro est déjà enregistré)
    setCustomInvoiceId(invoice.numero_facture != null ? String(invoice.numero_facture) : '');
    setUseCustomId(false);
    
    // Get payments to determine default payment method
    try {
      const paymentsResponse = await paymentsAPI.getPaymentsForRef('sales_invoice', invoice.id);
      const payments = paymentsResponse.data || [];
      if (payments.length > 0) {
        const firstPaymentMode = payments[0].mode;
        const paymentLabels = {
          'espece': 'Espèce',
          'cheque': 'Chèque',
          'carte_bancaire': 'Carte bancaire',
          'virement': 'Virement bancaire',
          'traite': 'Traite',
          'effet': 'Effet de commerce'
        };
        setSelectedPaymentMethod(paymentLabels[firstPaymentMode] || 'Espèce');
      } else {
        setSelectedPaymentMethod('Espèce');
      }
    } catch (err) {
      setSelectedPaymentMethod('Espèce');
    }
    
    // Show modal
    setShowInvoiceIdModal(true);
  };
  
  // Actually generate the PDF with the selected invoice ID
  const confirmGeneratePDF = async () => {
    if (!pendingInvoiceData) return;
    
    const { invoice, clientName } = pendingInvoiceData;
    const trimmedNumero = customInvoiceId.trim();
    
    let invoiceToUse = invoice;

    if (useCustomId && trimmedNumero) {
      try {
        await salesInvoicesAPI.updateSalesInvoice(invoice.id, {
          amount: invoice.amount,
          date: invoice.date ? invoice.date.slice(0, 10) : invoice.date,
          status: invoice.status,
          numero_facture: trimmedNumero
        });
        invoiceToUse = { ...invoice, numero_facture: trimmedNumero };
        toast.success('Numéro de facture enregistré');
        fetchInvoices();
      } catch (err) {
        console.error('Error saving invoice number:', err);
        const msg = err?.response?.data?.message || err?.message || '';
        toast.error(msg ? `Enregistrement impossible : ${msg}` : 'Impossible d\'enregistrer le numéro de facture');
        return;
      }
    }

    try {
      // Get payments for this invoice
      let payments = [];
      try {
        const paymentsResponse = await paymentsAPI.getPaymentsForRef('sales_invoice', invoice.id);
        payments = paymentsResponse.data || [];
      } catch (err) {
        console.log('No payments found for this invoice');
        payments = [];
      }
      
      // Use the invoice's custom items instead of sales order items
      const invoiceItems = invoice.items || [];
      
      // Enhance items with display names and prescription data
      let lensCount = 0; // Counter for lens items
      const enhancedItems = invoiceItems.map(item => {
        const enhancedItem = {
          ...item,
          product_name: item.custom_product_name || getProductName(item.product_id),
          price: item.unit_price
        };
        
        // Count lens items and add prescription data only to the SECOND lens item
        if (item.item_type === 'verre') {
          lensCount++;
          
          // Add prescription data only to the second lens item (when lensCount === 2)
          if (invoice && lensCount === 2) {
            enhancedItem.left_sph = invoice.left_sph;
            enhancedItem.left_cyl = invoice.left_cyl;
            enhancedItem.left_axe = invoice.left_axis;
            enhancedItem.left_addition = invoice.left_addition;
            enhancedItem.right_sph = invoice.right_sph;
            enhancedItem.right_cyl = invoice.right_cyl;
            enhancedItem.right_axe = invoice.right_axis;
            enhancedItem.right_addition = invoice.right_addition;
          }
        }
        
        return enhancedItem;
      });
      
      generateSalesInvoicePDF(invoiceToUse, clientName, enhancedItems, payments, selectedPaymentMethod);
      
      // Close modal
      setShowInvoiceIdModal(false);
      setPendingInvoiceData(null);
      setCustomInvoiceId('');
      setUseCustomId(false);
      setSelectedPaymentMethod('');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Factures (Ventes)</h1>
          <p className="text-neutral-600">Gérez les factures de vente avec personnalisation complète</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des factures..."
            onSearch={setSearchTerm}
            className="w-64"
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter une facture
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Factures</p>
              <p className="text-2xl font-bold text-neutral-900">{(invoices || []).length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Montant Total</p>
              <p className="text-2xl font-bold text-success-600">
                {formatCurrency(totalAmount)}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">En Attente</p>
              <p className="text-2xl font-bold text-warning-600">{pendingInvoices}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Payées</p>
              <p className="text-2xl font-bold text-info-600">{paidInvoices}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Invoices Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des factures</h3>
            <p className="text-sm text-neutral-600">{filteredInvoices.length} factures trouvées</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
            <ModernButton variant="ghost" icon={<Download className="w-4 h-4" />} onClick={handleExportPayments}>
              Export Paiements CSV
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredInvoices}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier la facture' : 'Ajouter une facture'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Numéro de facture"
              name="numero_facture"
              value={form.numero_facture}
              onChange={handleChange}
              placeholder="Vide = auto : année-id (ex. 2026-130, l’id est celui de la table)"
              maxLength={100}
            />

            <ModernSelect
              label="Commande"
              name="sales_id"
              value={salesOptions.find(opt => opt.value === form.sales_id) || null}
              onChange={option => {
                const selectedSale = salesOrders.find(s => s.id.toString() === option?.value);
                setForm(f => ({ 
                  ...f, 
                  sales_id: option ? option.value : '',
                  amount: selectedSale ? selectedSale.total.toString() : '',
                  items: [] // Clear items when changing sales order
                }));
                
                // Load items from the selected sales order
                if (option?.value) {
                  loadSalesOrderItems(option.value);
                }
              }}
              options={salesOptions}
              required
            />

            <ModernInput
              label="Montant Total"
              name="amount"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={handleChange}
              placeholder="0.00 MAD"
              required
            />

            <ModernInput
              label="Date"
              name="date"
              type="date"
              value={form.date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
              required
            />

            <ModernSelect
              label="Statut"
              name="status"
              value={statusOptions.find(opt => opt.value === form.status) || null}
              onChange={option => setForm(f => ({ ...f, status: option ? option.value : '' }))}
              options={statusOptions}
            />
          </div>

          {/* Editable Items Section */}
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Articles de la facture</h3>
                <p className="text-sm text-gray-500 mt-0.5">Glissez les lignes par la poignée pour changer l&apos;ordre d&apos;affichage (PDF et liste).</p>
              </div>
              <div className="flex items-center space-x-2">
                {form.sales_id && (
                  <ModernButton
                    type="button"
                    variant="ghost"
                    icon={<ShoppingCart className="w-4 h-4" />}
                    onClick={() => loadSalesOrderItems(form.sales_id)}
                    disabled={loadingItems}
                  >
                    Recharger depuis la commande
                  </ModernButton>
                )}
                <ModernButton
                  type="button"
                  variant="secondary"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={addItem}
                >
                  Ajouter un article
                </ModernButton>
              </div>
            </div>

            {loadingItems ? (
              <div className="text-center py-8">
                <ModernLoadingSpinner size="sm" />
                <p className="text-sm text-gray-500 mt-2">Chargement des articles...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {form.items && form.items.length > 0 ? (
                  form.items.map((item, index) => (
                    <div
                      key={item.clientRowId || `row-${index}`}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                      onDragOver={handleItemDragOver}
                      onDrop={(e) => handleItemDrop(e, index)}
                    >
                      <div className="flex gap-3">
                        <button
                          type="button"
                          draggable
                          onDragStart={(e) => handleItemDragStart(e, index)}
                          className="flex-shrink-0 self-start mt-7 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 cursor-grab active:cursor-grabbing"
                          title="Glisser pour réordonner"
                          aria-label="Réordonner la ligne"
                        >
                          <GripVertical className="w-5 h-5" />
                        </button>
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 flex-1 min-w-0">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom du produit
                          </label>
                          <ModernInput
                            value={item.custom_product_name}
                            onChange={(e) => updateItem(index, 'custom_product_name', e.target.value)}
                            placeholder="Nom personnalisé du produit"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <ModernSelect
                            value={itemTypes.find(opt => opt.value === item.item_type) || null}
                            onChange={option => updateItem(index, 'item_type', option ? option.value : 'monture')}
                            options={itemTypes}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Côté
                          </label>
                          <ModernSelect
                            value={sideOptions.find(opt => opt.value === item.side) || null}
                            onChange={option => updateItem(index, 'side', option ? option.value : 'none')}
                            options={sideOptions}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantité
                          </label>
                          <ModernInput
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Prix unitaire
                          </label>
                          <ModernInput
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price === '' || item.unit_price === 0 ? '' : item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', e.target.value === '' ? '' : parseFloat(e.target.value) || '')}
                            className="no-spinner"
                          />
                        </div>
                      </div>
                      </div>

                      <div className="flex items-center justify-between mt-4 pl-10">
                        <div className="text-sm text-gray-600">
                          Total: {formatCurrency(item.quantity * item.unit_price)}
                        </div>
                        <ModernButton
                          type="button"
                          variant="danger"
                          size="sm"
                          icon={<Trash2 className="w-4 h-4" />}
                          onClick={() => removeItem(index)}
                        >
                          Supprimer
                        </ModernButton>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {form.sales_id ? (
                      <div>
                        <p>Aucun article dans cette commande.</p>
                        <p className="text-sm mt-2">Cliquez sur "Ajouter un article" pour ajouter des articles personnalisés.</p>
                      </div>
                    ) : (
                      <div>
                        <p>Sélectionnez d'abord une commande pour voir ses articles.</p>
                        <p className="text-sm mt-2">Les articles de la commande s'afficheront ici et vous pourrez les modifier.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {form.items && form.items.length > 0 && (
              <div className="text-right text-lg font-semibold text-gray-900">
                Total facture: {formatCurrency(form.items.reduce((sum, item) => {
                  const price = item.unit_price === '' || item.unit_price === null || item.unit_price === undefined ? 0 : parseFloat(item.unit_price) || 0;
                  return sum + (item.quantity * price);
                }, 0))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <ModernModal 
          isOpen={showPaymentModal} 
          onClose={() => setShowPaymentModal(false)} 
          title={`Paiements pour facture #${selectedInvoice.id}`} 
          size="full" 
          className="max-w-3xl w-full"
        >
          <div className="space-y-6">
            {/* Totals Row */}
            <div className="flex flex-wrap items-center gap-6 border-b pb-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-neutral-700">Total payé:</span>
                <span className="text-lg font-bold text-success-700 flex items-center gap-1">
                  {formatCurrency((Array.isArray(invoicePayments) ? invoicePayments : []).reduce((sum, p) => sum + Number(p.amount), 0))}
                  {getPaymentStatus(selectedInvoice, Array.isArray(invoicePayments) ? invoicePayments : []).color === 'success' && <CheckCircle className="w-5 h-5 text-success-500" />}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-neutral-700">Reste à payer:</span>
                <span className="text-lg font-bold text-warning-700 flex items-center gap-1">
                  {formatCurrency(Math.max(0, (selectedInvoice.amount || 0) - (Array.isArray(invoicePayments) ? invoicePayments : []).reduce((sum, p) => sum + Number(p.amount), 0)))}
                  {getPaymentStatus(selectedInvoice, Array.isArray(invoicePayments) ? invoicePayments : []).color === 'warning' && <AlertCircle className="w-5 h-5 text-warning-500" />}
                </span>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-${getPaymentStatus(selectedInvoice, Array.isArray(invoicePayments) ? invoicePayments : []).color}-100 text-${getPaymentStatus(selectedInvoice, Array.isArray(invoicePayments) ? invoicePayments : []).color}-700 ml-2`}> 
                {(() => {
                  const paid = (Array.isArray(invoicePayments) ? invoicePayments : []).reduce((sum, p) => sum + Number(p.amount), 0);
                  const total = selectedInvoice.amount || 0;
                  if (paid === 0) return 'En attente';
                  if (paid < total) return 'Partiel';
                  return 'Payé';
                })()}
                {getPaymentStatus(selectedInvoice, Array.isArray(invoicePayments) ? invoicePayments : []).color === 'danger' && <XCircle className="w-4 h-4 ml-1" />}
              </span>
            </div>

            {/* Payment History */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2 text-primary-700">
                <Info className="w-4 h-4 text-primary-500" /> Historique des paiements
              </h4>
              {paymentLoading ? <ModernLoadingSpinner /> : (
                <div className="overflow-x-auto rounded-xl border bg-white">
                  <table className="min-w-full text-base">
                    <colgroup>
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '20%' }} />
                    </colgroup>
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="text-left font-semibold px-5 py-3">Date</th>
                        <th className="text-left font-semibold px-5 py-3">Montant</th>
                        <th className="text-left font-semibold px-5 py-3">Mode</th>
                        <th className="text-left font-semibold px-5 py-3">Note</th>
                        <th className="text-left font-semibold px-5 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(invoicePayments) ? invoicePayments : []).map((p, idx) => (
                        editingPaymentId === p.id ? (
                          <tr key={p.id} className="bg-yellow-50">
                            <td className="px-5 py-3">{new Date(p.payment_date).toLocaleString('fr-FR')}</td>
                            <td className="px-5 py-3">
                              <ModernInput
                                name="amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={editPayment.amount}
                                onChange={e => setEditPayment(ep => ({ ...ep, amount: e.target.value }))}
                                required
                              />
                            </td>
                            <td className="px-5 py-3">
                              <ModernSelect
                                name="mode"
                                value={paymentModes.find(opt => opt.value === editPayment.mode) || null}
                                onChange={option => setEditPayment(ep => ({ ...ep, mode: option ? option.value : '' }))}
                                options={paymentModes}
                              />
                            </td>
                            <td className="px-5 py-3">
                              <ModernInput
                                name="note"
                                value={editPayment.note}
                                onChange={e => setEditPayment(ep => ({ ...ep, note: e.target.value }))}
                              />
                            </td>
                            <td className="flex gap-2 px-5 py-3">
                              <ModernButton 
                                type="button" 
                                variant="success" 
                                size="sm" 
                                onClick={handleSaveEditPayment} 
                                icon={<CheckCircle className="w-4 h-4" />}
                              >
                                Enregistrer
                              </ModernButton>
                              <ModernButton 
                                type="button" 
                                variant="secondary" 
                                size="sm" 
                                onClick={cancelEditPayment} 
                                icon={<XCircle className="w-4 h-4" />}
                              >
                                Annuler
                              </ModernButton>
                            </td>
                          </tr>
                        ) : (
                          <tr key={p.id} className={idx % 2 === 0 ? 'bg-neutral-50 hover:bg-neutral-100' : 'bg-white hover:bg-neutral-100'}>
                            <td className="px-5 py-3 whitespace-nowrap align-middle">{new Date(p.payment_date).toLocaleString('fr-FR')}</td>
                            <td className="px-5 py-3 font-semibold text-primary-700 whitespace-nowrap align-middle">{formatCurrency(p.amount)}</td>
                            <td className="px-5 py-3 align-middle">
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                {p.mode === 'espece' && <Banknote className="w-4 h-4 text-success-500" />} 
                                {p.mode === 'cheque' && <Banknote className="w-4 h-4 text-info-500" />} 
                                {p.mode === 'carte_bancaire' && <CreditCard className="w-4 h-4 text-primary-500" />} 
                                <span>{paymentModes.find(m => m.value === p.mode)?.label || p.mode}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3 whitespace-pre-line align-middle">{p.note}</td>
                            <td className="flex gap-2 px-5 py-3 align-middle items-center">
                              <ModernButton 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => startEditPayment(p)} 
                                icon={<Edit className="w-4 h-4" />}
                              >
                                Modifier
                              </ModernButton>
                              <ModernButton 
                                type="button" 
                                variant="danger" 
                                size="sm" 
                                onClick={() => handleDeletePayment(p.id)} 
                                icon={<Trash2 className="w-4 h-4" />}
                              >
                                Supprimer
                              </ModernButton>
                            </td>
                          </tr>
                        )
                      ))}
                      {(Array.isArray(invoicePayments) ? invoicePayments : []).length === 0 && (
                        <tr>
                          <td colSpan={5} className="h-48 align-middle">
                            <div className="flex flex-col items-center justify-center h-full w-full">
                              <Info className="w-8 h-8 mb-2 text-primary-300" />
                              <span className="text-neutral-400 text-base">Aucun paiement enregistré pour cette facture.</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Add Payment Form */}
            <form onSubmit={handleAddPayment} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end bg-neutral-50 p-4 rounded-xl border-t">
              <ModernInput
                label="Montant (MAD)"
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                value={newPayment.amount}
                onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))}
                required
                icon={<Banknote className="w-4 h-4 text-primary-400" />}
              />
              <ModernSelect
                label="Mode"
                name="mode"
                value={paymentModes.find(opt => opt.value === newPayment.mode) || paymentModes[0]}
                onChange={option => setNewPayment(p => ({ ...p, mode: option ? option.value : '' }))}
                options={paymentModes}
                icon={<CreditCard className="w-4 h-4 text-primary-400" />}
              />
              <ModernInput
                label="Note"
                name="note"
                value={newPayment.note}
                onChange={e => setNewPayment(p => ({ ...p, note: e.target.value }))}
                icon={<Info className="w-4 h-4 text-primary-400" />}
              />
              <ModernButton 
                type="submit" 
                variant="primary" 
                disabled={paymentLoading} 
                icon={<Save className="w-4 h-4" />} 
                className="w-full md:w-auto"
              >
                {paymentLoading ? <ModernLoadingSpinner size="sm" /> : 'Ajouter paiement'}
              </ModernButton>
            </form>
          </div>
        </ModernModal>
      )}

      {/* Invoice ID Selection Modal */}
      {showInvoiceIdModal && pendingInvoiceData && (
        <ModernModal 
          isOpen={showInvoiceIdModal} 
          onClose={() => {
            setShowInvoiceIdModal(false);
            setPendingInvoiceData(null);
            setCustomInvoiceId('');
            setUseCustomId(false);
            setSelectedPaymentMethod('');
          }} 
          title="Options PDF Facture" 
          size="md"
        >
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-neutral-600">
                Configurez les options d'affichage du PDF facture
              </p>
            </div>

            <div className="space-y-4">
              {/* Original ID Option */}
              <div 
                onClick={() => {
                  setUseCustomId(false);
                  setCustomInvoiceId('');
                }}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  !useCustomId 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-neutral-200 hover:border-primary-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    checked={!useCustomId}
                    onChange={() => {
                      setUseCustomId(false);
                      setCustomInvoiceId('');
                    }}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900">
                      Conserver le numéro original
                    </h4>
                    <p className="text-sm text-neutral-600">
                      Utiliser le numéro enregistré :{' '}
                      <strong>
                        {pendingInvoiceData.invoice.numero_facture &&
                        String(pendingInvoiceData.invoice.numero_facture).trim() !== ''
                          ? String(pendingInvoiceData.invoice.numero_facture).trim()
                          : pendingInvoiceData.invoice.id}
                      </strong>
                      {!pendingInvoiceData.invoice.numero_facture && (
                        <span className="text-neutral-500 font-normal"> (interne)</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Custom ID Option */}
              <div 
                className={`p-4 border-2 rounded-lg transition-all ${
                  useCustomId 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-neutral-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    checked={useCustomId}
                    onChange={() => setUseCustomId(true)}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 mt-0.5"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900 mb-2">
                      Utiliser un nouveau numéro
                    </h4>
                    <p className="text-sm text-neutral-600 mb-3">
                      Saisissez un numéro personnalisé : il sera enregistré sur la facture puis utilisé pour le PDF.
                    </p>
                    <ModernInput
                      placeholder="Ex: FAC-2024-001"
                      value={customInvoiceId}
                      onChange={(e) => setCustomInvoiceId(e.target.value)}
                      disabled={!useCustomId}
                      className={!useCustomId ? 'opacity-50 cursor-not-allowed' : ''}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-neutral-900 mb-3">Mode de paiement</h4>
              <ModernSelect
                options={[
                  { value: 'Espèce', label: 'Espèce' },
                  { value: 'Carte bancaire', label: 'Carte bancaire' }
                ]}
                value={{ value: selectedPaymentMethod, label: selectedPaymentMethod }}
                onChange={(option) => setSelectedPaymentMethod(option ? option.value : 'Espèce')}
                placeholder="Sélectionner le mode de paiement"
              />
              <p className="text-xs text-neutral-500 mt-2">
                Ce mode de paiement sera affiché sur le PDF comme "PAYE EN [mode]"
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <ModernButton
                variant="secondary"
                onClick={() => {
                  setShowInvoiceIdModal(false);
                  setPendingInvoiceData(null);
                  setCustomInvoiceId('');
                  setUseCustomId(false);
                  setSelectedPaymentMethod('');
                }}
              >
                Annuler
              </ModernButton>
              <ModernButton
                variant="primary"
                onClick={confirmGeneratePDF}
                icon={<FileDown className="w-4 h-4" />}
                disabled={useCustomId && !customInvoiceId.trim()}
              >
                Générer le PDF
              </ModernButton>
            </div>
          </div>
        </ModernModal>
      )}
    </div>
  );
};

export default VentesFactures;